import { format, parseISO } from 'date-fns';
import { Camera, HardHat, Receipt, Wallet } from 'lucide-react';
import { AuthThumb, rowThumbProps } from '../../pages/diary/AuthThumb';
import { useVatBasis, VatBasisToggle, rowPence, totalsFor, BASIS_LABEL } from '../VatBasisToggle';

const gbp = (v, dp = 0) => `£${(Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const DISPLAY = { fontFamily: 'Barlow Condensed, sans-serif' };

function Empty({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center rounded-lg border border-dashed border-white/10 bg-[#2A3240]/50">
      <Icon className="w-8 h-8 text-gray-500 mb-2" />
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className="text-xs text-gray-400 max-w-sm mt-1">{text}</div>
    </div>
  );
}

const CAT_LABEL = { material: 'Material', plant: 'Plant', fuel: 'Fuel', other: 'Other' };

/** Costs tab: latest receipts (ledger — material, plant, fuel, other) + labour from the diary, side by side. */
export function LiveCostCards({ projectId, project, ledger, labourLog, labourSummary, navigate }) {
  const receipts = ledger?.entries || [];
  const people = labourLog?.by_person || [];
  const [basis, setBasis] = useVatBasis();
  const shown = totalsFor(ledger?.totals, basis);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-lg border border-white/10 bg-[#2A3240] p-5" data-testid="material-cost-log">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h2 className="text-lg font-semibold uppercase tracking-wider text-white inline-flex items-center gap-2" style={DISPLAY}><Camera className="w-4 h-4 text-[#F5A623]" />Materials &amp; plant</h2>
          <div className="flex items-center gap-2">
            <VatBasisToggle basis={basis} onChange={setBasis} size="xs" testid="material-cost-log-vat-toggle" />
            <span className="text-sm font-bold text-white tabular-nums" data-testid="material-cost-log-total">
              {gbp(shown.non_labour ?? shown.material, 2)} <span className="text-xs text-gray-500 font-normal">{BASIS_LABEL[basis]} · {ledger?.total || 0} {ledger?.total === 1 ? 'item' : 'items'}</span>
            </span>
          </div>
        </div>
        {receipts.length === 0 ? (
          <Empty icon={Receipt} title="Nothing logged yet" text="Materials &amp; plant costs captured in the Site Diary land here with their thumbnails." />
        ) : receipts.map(r => (
          <div key={r.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`material-log-row-${r.id}`}>
            <AuthThumb {...rowThumbProps(r)} className="w-10 h-10 rounded-md flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-100 font-medium truncate">{r.supplier_name || 'Unknown supplier'}</div>
              <div className="text-[11px] text-gray-500 truncate">
                {format(parseISO(r.invoice_date || r.created_at), 'dd MMM yyyy')}{r.reference ? ` · ${r.reference}` : ''}
                {CAT_LABEL[r.category] && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-gray-400" data-testid={`material-log-row-category-${r.id}`}>{CAT_LABEL[r.category]}</span>}
                {r.captured_by_name ? ` · ${r.captured_by_name}` : ''}
              </div>
            </div>
            <div className="text-sm font-semibold text-white tabular-nums" data-testid={`material-log-row-value-${r.id}`}>
              {r.value_pending ? <span className="text-[11px] text-amber-400">Pending</span> : gbp(rowPence(r, basis) / 100, 2)}
            </div>
          </div>
        ))}
        <div className="mt-3 flex justify-end items-center">
          <button type="button" onClick={() => navigate(`/cost-ledger?project_id=${projectId}&locked=1`)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/20 bg-white/5 text-gray-200 hover:bg-white/10 hover:text-white transition-colors" data-testid="material-log-open-ledger">
            <Wallet className="w-3.5 h-3.5 text-[#F5A623]" /> Cost Ledger{project?.project_ref ? ` · ${project.project_ref}` : ''}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#2A3240] p-5" data-testid="labour-cost-log">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold uppercase tracking-wider text-white inline-flex items-center gap-2" style={DISPLAY}><HardHat className="w-4 h-4 text-[#F5A623]" />Labour · diary + invoices</h2>
          <span className="text-sm font-bold text-white tabular-nums" data-testid="labour-cost-log-total">
            {gbp(labourLog?.total, 2)} <span className="text-xs text-gray-500 font-normal">· {labourLog?.person_days || 0} person-days</span>
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div className="rounded-md bg-[#1E2530] p-3" data-testid="labour-planned">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Planned · schedule</div>
            <div className="text-lg font-bold text-gray-200 tabular-nums" style={DISPLAY}>{gbp(labourSummary?.total_cost)}</div>
            <div className="text-[11px] text-gray-500">{labourSummary?.total_days || 0} days scheduled</div>
          </div>
          <div className="rounded-md bg-[#1E2530] p-3 border-l-2 border-emerald-400" data-testid="labour-diary-all">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">On site · diary (running)</div>
            <div className="text-lg font-bold text-emerald-300 tabular-nums" style={DISPLAY}>{gbp(labourLog?.diary_total)}</div>
            <div className="text-[11px] text-gray-500">{labourLog?.person_days || 0} person-days · everyone at day rate</div>
          </div>
          <div className="rounded-md bg-[#1E2530] p-3 border-l-2 border-[#F5A623]" data-testid="labour-actual">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Employees · diary</div>
            <div className="text-lg font-bold text-[#F5A623] tabular-nums" style={DISPLAY}>{gbp(labourLog?.diary_employee_total ?? labourLog?.diary_total)}</div>
            <div className="text-[11px] text-gray-500">{labourLog?.days_logged || 0} days logged · counted</div>
          </div>
          <div className="rounded-md bg-[#1E2530] p-3 border-l-2 border-sky-400" data-testid="labour-invoiced">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Confirmed · invoiced</div>
            <div className="text-lg font-bold text-sky-300 tabular-nums" style={DISPLAY}>{gbp(labourLog?.invoiced_total ?? labourLog?.ledger_total)}</div>
            <div className="text-[11px] text-gray-500">{labourLog?.ledger_rows?.length || 0} {labourLog?.ledger_rows?.length === 1 ? 'invoice / entry' : 'invoices / entries'} · counted</div>
          </div>
        </div>
        {(labourLog?.awaiting_invoice_total || 0) > 0 && (
          <div className="mb-3 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200 flex justify-between" data-testid="labour-awaiting-invoice">
            <span>Sub-contractor diary days · in the running total, counted only once their invoice is posted</span>
            <span className="tabular-nums font-semibold">{gbp(labourLog.awaiting_invoice_total, 2)}</span>
          </div>
        )}
        {people.length === 0 ? (
          <Empty icon={HardHat} title="No labour logged" text="People logged on site in the diary appear here costed at their day rate." />
        ) : (
          <>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 text-[10px] uppercase tracking-wider text-gray-500 pb-1 border-b border-white/10" data-testid="labour-log-person-header">
              <span>Person</span><span className="text-right">Diary</span><span className="text-right w-24">Invoiced</span>
            </div>
            {people.slice(0, 8).map(p => (
              <div key={p.person_id || p.name} className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center py-2 border-b border-white/5 last:border-0 text-sm" data-testid={`labour-log-person-${p.person_id || 'manual'}`}>
                <div className="min-w-0 text-gray-100 flex flex-wrap items-center gap-x-1.5">
                  <span className="truncate">{p.name}</span>{p.trade && <span className="text-gray-500 text-xs">{p.trade}</span>}
                  {p.counted === false && (p.invoiced_pence || 0) === 0 && p.days > 0 && <span className="text-[10px] uppercase tracking-wider text-amber-400/90 whitespace-nowrap" data-testid={`labour-log-person-awaiting-${p.person_id || 'manual'}`}>not yet invoiced</span>}
                </div>
                <div className="text-gray-400 text-xs tabular-nums text-right">{p.days > 0 ? <>{p.days}d × {gbp(p.rate)} = <span className={`font-semibold text-sm ${p.counted === false ? 'text-emerald-300' : 'text-white'}`}>{gbp(p.cost, 2)}</span></> : <span className="text-gray-600">—</span>}</div>
                <div className="text-right w-24 text-sm tabular-nums font-semibold text-sky-300" data-testid={`labour-log-person-invoiced-${p.person_id || 'manual'}`}>{(p.invoiced_pence || 0) > 0 ? gbp(p.invoiced, 2) : <span className="text-gray-600 font-normal">—</span>}</div>
              </div>
            ))}
          </>
        )}
        {(labourLog?.ledger_rows || []).slice(0, 6).map(r => (
          <div key={r.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-sm" data-testid={`labour-log-invoice-${r.id}`}>
            <div className="min-w-0 truncate text-gray-100">
              {r.supplier_name || (r.source === 'opening_balance' ? 'Opening balance' : r.source === 'manual' ? 'Manual entry' : 'Invoice')}
              <span className="text-gray-500 text-xs ml-1.5">{r.source === 'inbox' ? 'Cost Inbox' : r.source}{r.reference ? ` · ${r.reference}` : ''}{r.date ? ` · ${format(parseISO(r.date), 'dd MMM yyyy')}` : ''}</span>
            </div>
            <div className="text-sky-300 font-semibold text-sm tabular-nums">{gbp(r.value, 2)}</div>
          </div>
        ))}
        {labourLog?.missing_rate_count > 0 && (
          <div className="mt-2 text-[11px] text-amber-400" data-testid="labour-log-missing-rates">{labourLog.missing_rate_count} person-days have no day rate — set one in Staff Management</div>
        )}
        <div className="mt-3 text-right">
          <button type="button" onClick={() => navigate(`/diary/${projectId}/costs?view=labour`)} className="text-xs text-[#F5A623] hover:underline" data-testid="labour-log-open-diary">Day-by-day log in Site Diary →</button>
        </div>
      </div>
    </div>
  );
}
